/**
 * What comes back out of a verb, made safe for an agent to read.
 *
 * Three jobs, all of them here rather than in a verb so that no verb can forget,
 * which is the reason the design put them in the wrapper.
 *
 * One, shape. Requirement 11.7 lets a verb return a plain string or an MCP style
 * content array of text parts, and a host wants one of those two. Both come out of
 * here as a content array.
 *
 * Two, length. Requirement 11.3 limits an output to 1500 characters and asks for a
 * summary plus a stated means of retrieving the remainder past that. The cut lands
 * on a sentence boundary and the hint names the verb that returns the rest,
 * because an output truncated without a route onward is worse than a hard refusal:
 * the agent cannot tell it is missing anything.
 *
 * Three, failure. A thrown value becomes an `isError` result carrying a readable
 * message. A tool that throws takes the agent's turn down with it.
 *
 * The budget is `MAX_OUTPUT_CHARS` from `@jig/tools`, and the hint counts against
 * it: body plus hint is what the agent receives, so body plus hint is what fits.
 */

import { MAX_OUTPUT_CHARS, type ToolSpec, type ToolText } from '@jig/tools';

import { describeThrown, readProperty } from './host';

/** One text part, which is the only part type this surface produces. Req 11.7. */
const textPart = (text: string): { type: 'text'; text: string } => ({ type: 'text', text });

/**
 * The verb an agent calls to get what was cut, and the parameters that narrow it.
 *
 * Defaulted from the spec by {@link retrievalHintFor}, and overridable because the
 * verb that returns the remainder is not always the verb that ran out of room. A
 * full run of `read_pattern` overflows and the route onward is a per size
 * `read_pattern` call, which the default expresses; a verb whose remainder lives
 * behind a different name says so explicitly.
 */
export interface RetrievalHint {
  readonly verb: string;
  /** Parameters that narrow the call, in the order an agent should reach for them. */
  readonly narrowBy?: ReadonlyArray<string> | undefined;
}

/**
 * The hint a spec implies: its own name, narrowed by its optional parameters.
 *
 * A parameter that is not required is one that narrows a read, which is what makes
 * this a real route rather than an invitation to repeat the same call. A spec with
 * no optional parameter gets the verb alone.
 */
export const retrievalHintFor = <I>(spec: ToolSpec<I>): RetrievalHint => {
  const required = new Set<string>(spec.inputSchema.required ?? []);
  const narrowBy = Object.keys(spec.inputSchema.properties).filter((key) => !required.has(key));
  return narrowBy.length === 0 ? { verb: spec.name } : { verb: spec.name, narrowBy };
};

/** "size", "size or section", "size, section or form". */
const listNames = (names: ReadonlyArray<string>): string => {
  if (names.length <= 1) return names[0] ?? '';
  const last = names[names.length - 1] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${last}`;
};

/**
 * The sentence appended to a truncated output.
 *
 * Written in the register of the tool descriptions: what happened, then what to
 * call. It names the verb in every form, which is the part requirement 11.3 and
 * Property 10 check.
 */
export const retrievalSentence = (hint: RetrievalHint): string => {
  const names = hint.narrowBy ?? [];
  const shortened = `Output shortened to the ${MAX_OUTPUT_CHARS} character limit.`;
  return names.length === 0
    ? `${shortened} Call ${hint.verb} again for the remainder.`
    : `${shortened} Call ${hint.verb} again with ${listNames(names)} set to read the remainder.`;
};

/**
 * Where a sentence ends: terminal punctuation, any closing quote or bracket after
 * it, and then whitespace or the end of the text. Run over the whole string rather
 * than over the window, so that the character after a candidate cut is the real one
 * and a figure such as 40.5 is never mistaken for a full stop.
 */
const SENTENCE_END = /[.!?]["')\]]*(?=\s|$)/g;

const lastSentenceEnd = (text: string, budget: number): number | null => {
  let cut: number | null = null;
  for (const match of text.matchAll(SENTENCE_END)) {
    if (match.index === undefined) continue;
    const end = match.index + match[0].length;
    if (end > budget) break;
    cut = end;
  }
  return cut;
};

/**
 * How much of the budget a sentence boundary has to keep before it is preferred.
 *
 * A read verb's output is mostly lines of figures, and one full stop early in a
 * long list would otherwise throw away nearly everything that fit. Below this
 * share the cut falls back to a line break and then to a word break, so it is
 * never mid word either way.
 */
const MIN_KEPT_SHARE = 0.6;

const lastBreak = (text: string, budget: number): number => {
  const window = text.slice(0, budget);
  const line = window.lastIndexOf('\n');
  if (line > 0) return line;
  const word = window.lastIndexOf(' ');
  return word > 0 ? word : budget;
};

const cutPoint = (text: string, budget: number): number => {
  const sentence = lastSentenceEnd(text, budget);
  if (sentence !== null && sentence >= budget * MIN_KEPT_SHARE) return sentence;
  return Math.max(sentence ?? 0, lastBreak(text, budget));
};

const totalChars = (text: ToolText): number =>
  text.content.reduce((sum, part) => sum + part.text.length, 0);

/**
 * Hold an output inside the character budget, per requirement 11.3.
 *
 * Under the budget the result is returned as it came. Over it, the parts are joined,
 * cut at the last sentence boundary that fits, and the retrieval sentence is
 * appended, with the sentence itself counted against the budget so what the agent
 * receives is at most `MAX_OUTPUT_CHARS`.
 */
export const boundToolText = (text: ToolText, hint: RetrievalHint): ToolText => {
  if (totalChars(text) <= MAX_OUTPUT_CHARS) return text;

  const sentence = retrievalSentence(hint);
  const joined = text.content.map((part) => part.text).join('\n');
  const budget = Math.max(MAX_OUTPUT_CHARS - sentence.length - 1, 0);
  const body = joined.slice(0, cutPoint(joined, budget)).trimEnd();

  return {
    content: [textPart(body === '' ? sentence : `${body}\n${sentence}`)],
    ...(text.isError === undefined ? {} : { isError: text.isError }),
  };
};

/**
 * A JSON string that turns out to be a whole tool result, or null where it is not.
 *
 * This is Chrome's answer shape, and it was a surprise. On Chrome Canary
 * 154.0.8032.0 with `--enable-features=WebMCP` a resolved `executeTool` promise
 * carries a **string**, and the string is the JSON of the `{ content: [...] }`
 * envelope the tool returned. So a reader that only understood the object would take
 * a refusal, with its rule and its repair values in it, and hand a caller the whole
 * envelope as if it were prose.
 *
 * The test is deliberately narrow: the text has to parse, and what it parses to has
 * to carry a `content` array. Everything else stays a plain string, so a verb whose
 * own answer happens to be valid JSON, which `substitute_yarn` reads out of the yarn
 * service on every call, is still one text part rather than an envelope this function
 * invented.
 */
const parsedEnvelope = (text: string): unknown => {
  const trimmed = text.trim();
  // A cheap gate before the parse. Every envelope is an object literal, and the read
  // verbs answer with prose, which would otherwise be handed to `JSON.parse` on every
  // call for nothing.
  if (!trimmed.startsWith('{')) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  return Array.isArray(readProperty(parsed, 'content')) ? parsed : null;
};

/**
 * Normalise what a verb returned, or what a host answered, or null where it cannot be
 * read as text.
 *
 * A string becomes one text part, unless it is the JSON of a content array, which is
 * how Chrome hands a tool result back to `executeTool`. See {@link parsedEnvelope}. A
 * content array is rebuilt part by part, so a part carrying anything other than a
 * string `text` is rejected rather than silently stringified. The types forbid both,
 * and a host hands an untyped value to an untyped closure, so the check is here for
 * the day one of them is wrong.
 */
export const asToolText = (value: unknown): ToolText | null => {
  if (typeof value === 'string') {
    const envelope = parsedEnvelope(value);
    return envelope === null ? { content: [textPart(value)] } : asToolText(envelope);
  }

  const content = readProperty(value, 'content');
  if (!Array.isArray(content)) return null;

  const parts: Array<{ type: 'text'; text: string }> = [];
  for (const raw of content) {
    const text = readProperty(raw, 'text');
    if (typeof text !== 'string') return null;
    parts.push(textPart(text));
  }

  return {
    content: parts,
    ...(readProperty(value, 'isError') === true ? { isError: true } : {}),
  };
};

/** An `isError` result carrying a sentence a person and an agent can both read. */
export const errorToolText = (message: string): ToolText => ({
  content: [textPart(message)],
  isError: true,
});

/**
 * A thrown value as an `isError` result.
 *
 * The error name is kept alongside the message, for the same reason requirement 1.8
 * wants it on a registration failure: `NotAllowedError` and `TypeError` tell an
 * agent different things about whether to try again. A thrown string, a thrown
 * number and a rejected promise all arrive here and all come out readable.
 */
export const thrownToolText = (verb: string, thrown: unknown): ToolText => {
  const { name, message } = describeThrown(thrown);
  const detail = message === '' ? name : `${name}: ${message}`;
  return errorToolText(`${verb} could not complete. ${detail}`);
};
